import { CheckCircle2, Circle, CircleDot } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useState, type CSSProperties } from "react";
import { useParams } from "react-router";

import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { ProgressBar, useFormatDate } from "../common/components";
import { StylePreview } from "../styles/StylePreview";
import type { Style } from "../types";

type Payload =
  | { kind: "style"; style: Pick<Style, "name" | "description" | "tokens"> }
  | {
      kind: "deal_progress";
      deal: { name: string; company: string | null };
      delivery: { progress: number; next_due_date?: string | null } | null;
      milestones: {
        title: string;
        description?: string | null;
        due_date?: string | null;
        status: "pending" | "in_progress" | "done";
        progress: number;
        nb_items: number;
        nb_items_done: number;
      }[];
    };

/** Read-only page a client opens from a share link, without signing in. */
export const PublicSharePage = () => {
  const { token } = useParams();
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const { lightModeLogo, title } = useConfigurationContext();
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "error" }
    | { status: "ok"; data: Payload }
  >({ status: "loading" });

  useEffect(() => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public_share?token=${encodeURIComponent(token ?? "")}`;
    fetch(url, {
      headers: { apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY ?? "" },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: Payload) => setState({ status: "ok", data }))
      .catch(() => setState({ status: "error" }));
  }, [token]);

  if (state.status === "loading") return null;

  return (
    // Light palette whatever theme the visitor's browser has (the app may be in dark mode).
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      style={
        {
          "--muted": "#e2e8f0",
          "--muted-foreground": "#64748b",
        } as CSSProperties
      }
    >
      <div className="mx-auto max-w-4xl space-y-8 p-6 md:p-10">
        {state.status === "error" ? (
          <p className="py-24 text-center text-slate-500">
            {translate("hub.share.not_found")}
          </p>
        ) : state.data.kind === "style" ? (
          <>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold">
                {state.data.style.name}
              </h1>
              {state.data.style.description && (
                <p className="text-slate-600">{state.data.style.description}</p>
              )}
            </div>
            <StylePreview
              tokens={state.data.style.tokens}
              className="shadow-xl"
            />
          </>
        ) : (
          <>
            <div className="space-y-1">
              {state.data.deal.company && (
                <p className="text-sm uppercase tracking-wide text-slate-500">
                  {state.data.deal.company}
                </p>
              )}
              <h1 className="text-3xl font-semibold">{state.data.deal.name}</h1>
              <p className="text-slate-600">
                {translate("hub.share.progress_title")}
              </p>
            </div>
            {state.data.delivery && (
              <div className="rounded-xl border bg-white p-5 shadow-sm">
                <ProgressBar
                  value={state.data.delivery.progress}
                  className="text-base"
                />
                {state.data.delivery.next_due_date && (
                  <p className="mt-2 text-sm text-slate-500">
                    {translate("hub.delivery.next_due", {
                      date: formatDate(state.data.delivery.next_due_date),
                    })}
                  </p>
                )}
              </div>
            )}
            <ol className="space-y-3">
              {state.data.milestones.map((m, i) => (
                <li
                  key={i}
                  className="rounded-xl border bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    {m.status === "done" ? (
                      <CheckCircle2 className="mt-0.5 size-5 text-green-600" />
                    ) : m.status === "in_progress" ? (
                      <CircleDot className="mt-0.5 size-5 text-blue-600" />
                    ) : (
                      <Circle className="mt-0.5 size-5 text-slate-400" />
                    )}
                    <div className="flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-medium">{m.title}</p>
                        <p className="text-sm text-slate-500">
                          {formatDate(m.due_date)} ·{" "}
                          {translate(`hub.status.${m.status}`)}
                        </p>
                      </div>
                      {m.description && (
                        <p className="text-sm text-slate-600">
                          {m.description}
                        </p>
                      )}
                      <ProgressBar value={m.progress} />
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
        <footer className="flex items-center justify-center gap-2 pt-6 text-xs text-slate-400">
          <img src={lightModeLogo} alt={title} className="size-4" />
          {translate("hub.share.powered_by")}
        </footer>
      </div>
    </div>
  );
};

PublicSharePage.path = "/p/:token";
